import {
  Cartesian2,
  Cartographic,
  Math as CesiumMath,
  ScreenSpaceEventHandler,
  ScreenSpaceEventType,
} from 'cesium';
import { isUndefined } from 'lodash';
import type { CesiumViewerType, CVTNodeStyle } from '@aus-platform/cesium';
import type { CartographicPosition } from './types';
import type { LayerNode } from '../api/layers/types';

/**
 * Call `handler` only for deliberate left-clicks on the Cesium canvas, with the
 * click position in canvas coordinates.
 *
 * The browser's native `click` fires for any press/release pair over the same
 * element, however far the pointer travelled in between — so dropping a dragged
 * vertex counts as a "click" at the drop point. Cesium's LEFT_CLICK instead
 * requires the release to land within its click pixel tolerance of the press,
 * so drags never reach the handler. That is also the gesture test the rest of
 * the map already uses (MapTool.canvasClickEvent), which keeps click consumers
 * in agreement about what counts as a click.
 *
 * @returns a disposer that unregisters the handler.
 */
export const addCanvasClickListener = (
  canvas: HTMLCanvasElement,
  handler: (position: Cartesian2) => void,
): (() => void) => {
  const eventHandler = new ScreenSpaceEventHandler(canvas);

  eventHandler.setInputAction(
    ({ position }: ScreenSpaceEventHandler.PositionedEvent) => {
      handler(Cartesian2.clone(position, new Cartesian2()));
    },
    ScreenSpaceEventType.LEFT_CLICK,
  );

  return () => {
    eventHandler.destroy();
  };
};

export const getCartographicPositionFromMousePosition = (
  mouseWindowPosition,
  viewer: CesiumViewerType | undefined,
): CartographicPosition | undefined => {
  if (!isUndefined(mouseWindowPosition) && viewer) {
    // Create a ray from the camera position through the pixel at mouseWindowPosition in world coordinates.
    const ray = viewer.scene.camera.getPickRay(mouseWindowPosition);

    if (isUndefined(ray)) {
      return;
    }

    // Find an intersection between a ray and the globe surface that was rendered. The ray must be given in world coordinates.
    const cartesian = viewer.scene.globe.pick(ray, viewer.scene);
    if (isUndefined(cartesian)) {
      return;
    }
    if (cartesian) {
      // Creates a new Cartographic instance from a Cartesian position. The values in the resulting object will be in radians.
      const cartographic = Cartographic.fromCartesian(cartesian);

      const longitude = Number(
        CesiumMath.toDegrees(cartographic.longitude).toFixed(7),
      );
      const latitude = Number(
        CesiumMath.toDegrees(cartographic.latitude).toFixed(7),
      );
      let altitude = Number(
        viewer.scene.globe.getHeight(cartographic)?.toFixed(2),
      );

      altitude = isNaN(altitude) ? 0 : altitude;

      return { longitude, latitude, altitude };
    }
  }
};

export const buildNodeStylesMap = (
  nodes?: LayerNode[],
): Map<string, CVTNodeStyle> => {
  const map = new Map<string, CVTNodeStyle>();
  nodes?.forEach((node) => {
    map.set(node.id, node.styles);
  });
  return map;
};
